// Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
// Styles
import { concatStyleSets, keyframes, memoizeFunction, mergeStyles } from "@fluentui/react";
import { bubble, confetti, confettiAnimationDuration } from "../../../utilities/ConfettiAnimation.styles";
import { flexParent, flexParentCenterAll } from "../../../utilities/Mixin.styles";
// Utiltiies
import { isLoopTheme } from "../../../utilities/ThemingUtilities";
/**
 * Helper to get styles for CompleteTaskAnimationButton
 * @param theme Current app theme
 * @param isTaskCompleted True if task is completed
 * @param isClicked True if button is being clicked
 * @param isDisabled True if button is disabled
 * @param customStyles Any custom style overrides
 */ export const getStyles = memoizeFunction((theme, isTaskCompleted, isClicked, isDisabled, customStyles)=>{
    const hasLoopTheme = isLoopTheme(theme);
    const root = [
        flexParent(),
        flexParentCenterAll,
        {
            margin: 0,
            padding: 0,
            background: "transparent",
            border: "0px",
            cursor: "pointer",
            overflow: "visible",
            ":hover": {
                ".completeButtonIcon": {
                    display: "none"
                },
                ".hoveredCompleteButtonIcon": {
                    display: hasLoopTheme ? "flex !important" : "flex"
                }
            }
        },
        isDisabled && {
            pointerEvents: "none"
        }
    ];
    const toggleCheckLabel = [
        flexParent(),
        {
            position: "relative",
            alignSelf: "center",
            cursor: "pointer",
            color: toggleCheckLabelColor(theme, isTaskCompleted, isDisabled),
            ":before, :after": {
                position: "absolute",
                top: "50%",
                left: "50%",
                borderRadius: "50%",
                content: "''"
            },
            ":before": {
                // Used for bubble animation
                boxSizing: "border-box",
                margin: `-${bubbleRadius}rem`,
                borderStyle: "solid",
                borderWidth: `${bubbleRadius}rem`,
                borderColor: hoveredCompleteButtonIconColor(theme, isTaskCompleted),
                width: `${bubbleDiameter}rem`,
                height: `${bubbleDiameter}rem`,
                transform: "scale(0)"
            },
            ":after": [
                // Used for confetti animation
                taskConfetti(1, theme),
                taskConfettiPosition,
                {
                    content: "''"
                }
            ]
        },
        isTaskCompleted && [
            {
                ":before, :after": {
                    animation: "inherit",
                    animationTimingFunction: "ease-out",
                    animationDuration: confettiAnimationDuration
                }
            },
            isClicked && {
                ":before": {
                    animationName: taskBubbleAnimation
                },
                ":after": {
                    animationName: taskConfettiAnimation(theme)
                }
            }
        ]
    ];
    const buttonIconCommonStyles = {
        ":active": [
            {
                // Scale down the icon on mouse press
                transform: "scale(0.9)",
                transitionDuration: "0.1s"
            }
        ]
    };
    const completeButtonIcon = [
        buttonIconCommonStyles,
        isDisabled && isTaskCompleted && {
            color: disabledButtonColor(theme)
        }
    ];
    const hoveredCompleteButtonIcon = [
        buttonIconCommonStyles,
        {
            display: hasLoopTheme ? "none !important" : "none",
            color: hoveredCompleteButtonIconColor(theme, isTaskCompleted)
        }
    ];
    const CompleteTaskAnimationButtonStyles = {
        root,
        toggleCheckLabel,
        completeButtonIcon,
        hoveredCompleteButtonIcon
    };
    return concatStyleSets(CompleteTaskAnimationButtonStyles, customStyles);
});
export const completeTaskButtonClassName = "completeButtonWithAnimation";
/**
 * Get the classnames associated with the computed styles
 * @param styles The styles for the component
 */ export const getClassNames = memoizeFunction((styles)=>({
        root: mergeStyles(completeTaskButtonClassName, styles.root),
        toggleCheckLabel: mergeStyles("toggleCheckLabel", styles.toggleCheckLabel),
        completeButtonIcon: mergeStyles("completeButtonIcon", styles.completeButtonIcon),
        hoveredCompleteButtonIcon: mergeStyles("hoveredCompleteButtonIcon", styles.hoveredCompleteButtonIcon)
    }));
/**
 * Get color for the disabled button
 * @param theme current app theme
 */ const disabledButtonColor = (theme)=>theme.name !== PlannerTheme.HighContrast ? theme.palette.neutralTertiary : Colors.TeamsContrastPlaceHolderOrDisabledColor;
/**
 * Get styles for toggleCheckLabel component
 * @param theme current app theme
 * @param isTaskCompleted whether the task is marked complete
 * @param isDisabled whether the button is disabled
 */ const toggleCheckLabelColor = (theme, isTaskCompleted, isDisabled)=>{
    if (isDisabled) {
        return disabledButtonColor(theme);
    }
    if (!isTaskCompleted) {
        return theme.palette.neutralSecondary;
    } else {
        switch(theme.name){
            case PlannerTheme.Light:
                return Colors.TeamsAppBrand00;
            case PlannerTheme.Dark:
                return Colors.TeamsAppBrand08;
            case PlannerTheme.HighContrast:
                return Colors.TeamsContrastHyperlinkColor;
            default:
                return Colors.ThemePrimary;
        }
    }
};
const hoveredCompleteButtonIconColor = (theme, isTaskCompleted)=>{
    switch(theme.name){
        case PlannerTheme.Light:
            return Colors.TeamsAppBrand04;
        case PlannerTheme.Dark:
            return Colors.TeamsAppBrand00;
        case PlannerTheme.HighContrast:
            return Colors.TeamsContrastHyperlinkColor;
        default:
            return Colors.CompleteButtonHoverColor;
    }
};
// Animation constants
const bubbleDiameter = 1.5; // rem
const bubbleRadius = 0.5 * bubbleDiameter; // rem
const confettiDiameter = 0.4; // rem
const confettiRadius = 0.5 * confettiDiameter; // rem
const numGroups = 6;
// x/y components of confetti direction vector
const cosines = [
    0,
    0.8660254,
    0.8660254,
    0,
    -0.8660254,
    -0.8660254
];
const sines = [
    -1,
    -0.5,
    0.5,
    1,
    0.5,
    -0.5
];
const defaultColors = [
    Colors.ConfettiRed,
    Colors.ConfettiOrange,
    Colors.ConfettiGreen,
    Colors.ConfettiBlue,
    Colors.ConfettiPurple,
    Colors.ConfettiMagenta
];
const teamsLightColors = [
    Colors.TeamsAppBrand12,
    Colors.TeamsAppBrand00,
    Colors.TeamsAppBrand12,
    Colors.TeamsAppBrand00,
    Colors.TeamsAppBrand12,
    Colors.TeamsAppBrand00
];
const teamsDarkColors = [
    Colors.TeamsAppBrand12,
    Colors.TeamsDarkThemeConfettiDarkPurple,
    Colors.TeamsAppBrand12,
    Colors.TeamsDarkThemeConfettiDarkPurple,
    Colors.TeamsAppBrand12,
    Colors.TeamsDarkThemeConfettiDarkPurple
];
const teamsContrastColors = [
    Colors.White,
    Colors.White,
    Colors.White,
    Colors.White,
    Colors.White,
    Colors.White
];
const taskBubbleAnimation = keyframes({
    "15%": bubble(bubbleRadius),
    "30%, 100%": bubble(0)
});
/**
 * Complete task button confetti styles
 * @param k Animation interpolation value between 0 - 1
 * @param theme Planner theme
 */ const taskConfetti = (k, theme)=>{
    let colors;
    switch(theme.name){
        case PlannerTheme.Light:
            colors = teamsLightColors;
            break;
        case PlannerTheme.Dark:
            colors = teamsDarkColors;
            break;
        case PlannerTheme.HighContrast:
            colors = teamsContrastColors;
            break;
        default:
            colors = defaultColors;
            break;
    }
    return confetti(k, bubbleRadius, confettiDiameter, numGroups, cosines, sines, colors);
};
// Confetti animation keyframes
const taskConfettiAnimation = memoizeFunction((theme)=>keyframes({
        "0%, 20%": {
            opacity: 0
        },
        "25%": {
            opacity: 1,
            ...taskConfetti(0, theme)
        }
    }));
// Confetti position
const taskConfettiPosition = {
    margin: `-${confettiRadius}rem`,
    marginTop: `${confettiRadius / 1.25}rem`,
    width: `${confettiDiameter}rem`,
    height: `${confettiDiameter}rem`
};
